import React, { useState, useEffect } from 'react'

const PriceManager = ({ products, onPriceUpdate }) => {
  const [prices, setPrices] = useState({})
  const [saved, setSaved] = useState(false)

  // Load prices from localStorage on mount
  useEffect(() => {
    const savedPrices = localStorage.getItem('customPrices')
    if (savedPrices) {
      setPrices(JSON.parse(savedPrices))
    } else {
      // Initialize with current product prices
      const initialPrices = {}
      products.forEach(product => {
        initialPrices[product.id] = product.price
      })
      setPrices(initialPrices)
    }
  }, [products])

  const handlePriceChange = (productId, value) => {
    const numValue = parseFloat(value) || 0
    setPrices({
      ...prices,
      [productId]: numValue
    })
    setSaved(false)
  }

  const handleSave = () => {
    localStorage.setItem('customPrices', JSON.stringify(prices))
    onPriceUpdate(prices)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleReset = () => {
    const defaultPrices = {}
    products.forEach(product => {
      defaultPrices[product.id] = product.price
    })
    setPrices(defaultPrices)
    localStorage.removeItem('customPrices')
    onPriceUpdate(defaultPrices)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const mainProducts = products.filter(p => p.category === 'Main')
  const externalProducts = products.filter(p => p.category === 'External')

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Price Management</h2>
        <div className="space-x-4">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Save Prices
          </button>
        </div>
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-300 rounded-md p-4">
          <p className="text-green-800">✓ Prices saved successfully!</p>
        </div>
      )}

      {/* Main Products Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Main Products</h3>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price (€/month)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mainProducts.map((product) => (
                <tr key={product.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {product.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {product.variant}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {product.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      value={prices[product.id] || 0}
                      onChange={(e) => handlePriceChange(product.id, e.target.value)}
                      className="w-24 px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      step="1"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* External Services Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Services</h3>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price (€/month)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {externalProducts.map((product) => (
                <tr key={product.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {product.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {product.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      value={prices[product.id] || 0}
                      onChange={(e) => handlePriceChange(product.id, e.target.value)}
                      className="w-24 px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      step="1"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default PriceManager